import { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ExternalLink } from 'lucide-react'
import type { SystemAdminApi } from '../adminApi.js'
import { LoadFailure, Skeleton } from '../../core/index.js'

/** The admin calls this screen uses. */
type AdminApi = SystemAdminApi

/** The reference rarely changes within a sitting, and it is a large page: read once and kept. */
const DOCS_STALE_MS = 10 * 60 * 1000

/**
 * The API reference, read inside the admin area. The API serves it as one
 * self-contained HTML page (`GET /admin/docs`, with the admin's own
 * credential), so it is fetched like any other admin call and shown in a
 * frame of its own, where its styles and its menu cannot touch the admin
 * shell's. The frame is given the page as a local object URL rather than
 * the API's address, since a frame cannot send the credential.
 */
export function ApiDocsPage({ api }: { api: AdminApi }) {
  const docsQuery = useQuery({ queryKey: ['admin', 'api-docs'], queryFn: () => api.readApiDocs(), staleTime: DOCS_STALE_MS })
  const pageUrl = useMemo(() => (docsQuery.data ? URL.createObjectURL(new Blob([docsQuery.data], { type: 'text/html' })) : null), [docsQuery.data])
  useEffect(() => {
    return () => {
      if (pageUrl) {
        URL.revokeObjectURL(pageUrl)
      }
    }
  }, [pageUrl])

  if (docsQuery.isPending) {
    return <Skeleton lines={5} label="Loading the API reference" className="max-w-5xl mx-auto px-7 py-6" />
  }
  if (docsQuery.isError || !pageUrl) {
    return <LoadFailure className="max-w-5xl mx-auto px-7 py-6" message={docsQuery.error instanceof Error ? docsQuery.error.message : 'The API reference could not be loaded.'} onRetry={() => void docsQuery.refetch()} retrying={docsQuery.isFetching} />
  }
  return (
    <div className="px-7 py-4">
      <div className="mb-2 flex items-center justify-between gap-4 text-[11px] text-ink-mid">
        <span>The reference the API serves at /docs, for every endpoint in this build.</span>
        <a href={pageUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-bold text-marine hover:text-marine-dk">
          <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
          Open in its own tab
        </a>
      </div>
      {/* Scripts run (the page's own menu and copy buttons) but in a sandbox with no access to this origin: the frame cannot reach the admin's credential or storage. */}
      <iframe title="API reference" src={pageUrl} sandbox="allow-scripts allow-popups" className="w-full h-[calc(100vh-13rem)] min-h-[32rem] border border-marine/15 bg-white" />
    </div>
  )
}
